import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useDoctorProfileAccess } from '@/features/doctor-profiles/access';
import {
  useDoctorProfile,
  useSaveDoctorProfile,
} from '@/features/doctor-profiles/api';
import {
  DOCTOR_PROFILE_LIMITS,
  type DoctorProfile,
  type PutDoctorProfileInput,
} from '@/features/doctor-profiles/types';
import { isApiError } from '@/lib/api';
import local from '@/pages/finance/finance.module.css';
import { DoctorPhotoField } from './DoctorPhotoField';
import {
  apiFieldErrors,
  hasErrors,
  move,
  toPutFields,
  validateProfile,
  type DoctorProfileErrors,
} from './doctor-profile-form';
import styles from './DoctorProfileDrawer.module.css';
import { DoctorDetailsSection } from './DoctorDetailsSection';

/** What 404 / 422 mean here; the API's own message is shown below it. */
const LOAD_ERRORS: Record<string, { title: string; body: string }> = {
  STAFF_NOT_FOUND: {
    title: 'Doctor not found',
    body: 'This staff member does not exist, or is not a doctor at one of your branches.',
  },
  STAFF_NOT_DOCTOR: {
    title: 'Not a doctor',
    body: 'Only staff with the doctor role have a public profile in the app.',
  },
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-MY', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

/**
 * "Public profile" of a doctor (RL-1160): the clinic-approved qualifications
 * and photo the patient app shows. Read with `staff:read` or
 * `doctor_profiles:read`, edit with `staff:manage` or `doctor_profiles:manage`
 * (clinic managers: the doctors of their branches).
 */
export function DoctorProfileDrawer({
  staffId,
  name,
  onClose,
}: {
  staffId: string;
  name: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const formId = useId();
  const profile = useDoctorProfile(staffId);
  const save = useSaveDoctorProfile(staffId);
  const { canEdit } = useDoctorProfileAccess();
  const [revision, setRevision] = useState(0);
  const [failure, setFailure] = useState<{
    code: string;
    message: string;
    requestId?: string;
  } | null>(null);
  const p = profile.data;
  const editing = !!p && canEdit;
  const conflict = failure?.code === 'DOCTOR_PROFILE_CHANGED';
  const known = isApiError(profile.error)
    ? LOAD_ERRORS[profile.error.code]
    : undefined;

  /** Saves; resolves the API's field errors (if any) for the form. */
  async function persist(
    fields: Omit<PutDoctorProfileInput, 'updatedAt'>,
  ): Promise<DoctorProfileErrors | null> {
    setFailure(null);
    try {
      await save.mutateAsync({ ...fields, updatedAt: p?.updatedAt ?? null });
      toast.show({ tone: 'success', title: 'Public profile saved' });
      onClose();
      return null;
    } catch (err) {
      if (!isApiError(err)) {
        setFailure({ code: '', message: 'Could not save the profile.' });
        return null;
      }
      setFailure({
        code: err.code,
        message:
          LOAD_ERRORS[err.code]?.body ??
          (err.code === 'DOCTOR_PROFILE_CHANGED'
            ? 'Someone else saved this profile after you opened it. Reload to see their version; your changes here are not saved.'
            : err.message),
        requestId: err.requestId,
      });
      return err.fieldErrors.length ? apiFieldErrors(err.fieldErrors) : null;
    }
  }

  const notice = failure && (
    <div
      className={[styles.note, conflict && styles.conflict]
        .filter(Boolean)
        .join(' ')}
      role="alert"
    >
      {failure.message}
      {failure.requestId && (
        <span>{t('common.requestId', { id: failure.requestId })}</span>
      )}
      {conflict && (
        <div className={styles.noteActions}>
          <Button
            size="sm"
            loading={profile.isFetching}
            onClick={() =>
              void profile.refetch().then(() => {
                setFailure(null);
                setRevision((v) => v + 1);
              })
            }
          >
            Reload profile
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!editing}
      title={`Public profile · ${p?.name ?? name}`}
      footer={
        editing ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              loading={save.isPending}
              disabled={conflict}
            >
              Save
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {p && (
        <DoctorDetailsSection
          key={p.detailsVersion ?? 'details'}
          staffId={staffId}
          profile={p}
          canEdit={canEdit}
        />
      )}
      {p ? (
        <ProfileBody
          // A reload after 409 brings a new updatedAt: start again from it.
          key={`${staffId}-${revision}`}
          profile={p}
          staffId={staffId}
          canEdit={canEdit}
          formId={formId}
          notice={notice}
          onSave={persist}
        />
      ) : profile.isLoading ? (
        <LoadingState label="Loading the public profile" />
      ) : (
        <div className={local.form}>
          {known && <p>{known.body}</p>}
          <ErrorState
            error={profile.error}
            title={known?.title}
            onRetry={known ? undefined : () => void profile.refetch()}
          />
        </div>
      )}
    </Dialog>
  );
}

let nextLineId = 0;
const newLine = (text: string) => ({ id: nextLineId++, text });

function ProfileBody({
  profile: p,
  staffId,
  canEdit,
  formId,
  notice,
  onSave,
}: {
  profile: DoctorProfile;
  staffId: string;
  canEdit: boolean;
  formId: string;
  notice: ReactNode;
  onSave: (
    fields: Omit<PutDoctorProfileInput, 'updatedAt'>,
  ) => Promise<DoctorProfileErrors | null>;
}) {
  const [lines, setLines] = useState(() => p.qualifications.map(newLine));
  const [errors, setErrors] = useState<DoctorProfileErrors>({ lines: {} });
  const listRef = useRef<HTMLOListElement>(null);
  const max = DOCTOR_PROFILE_LIMITS.maxLines;

  const edit = (next: typeof lines) => {
    setLines(next);
    setErrors((e) => ({ ...e, lines: {}, list: undefined }));
  };
  const focusLine = (index: number) =>
    requestAnimationFrame(() =>
      listRef.current
        ?.querySelectorAll<HTMLInputElement>('input')
        [index]?.focus(),
    );

  async function submit() {
    const texts = lines.map((l) => l.text);
    const invalid = validateProfile(texts);
    setErrors(invalid);
    if (hasErrors(invalid)) return;
    const fromApi = await onSave(toPutFields(texts));
    if (fromApi) setErrors(fromApi);
  }

  const configured = p.profileState === 'configured';
  const content = (
    <>
      <p className={styles.summary}>
        <Badge tone={configured ? 'success' : 'neutral'}>
          {configured ? 'Shown in the app' : 'Not set up'}
        </Badge>
        <span>
          {p.updatedAt
            ? `Last updated ${when(p.updatedAt)}${p.updatedBy ? ` by ${p.updatedBy.name}` : ''}`
            : 'Never updated'}
        </span>
        {p.mmcNumber && <span>{p.mmcNumber}</span>}
      </p>
      {notice}

      <h3 className={styles.section}>Qualifications</h3>
      {!canEdit ? (
        p.qualifications.length ? (
          <ol className={styles.readList}>
            {p.qualifications.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ol>
        ) : (
          <p className={styles.muted}>No qualifications yet.</p>
        )
      ) : (
        <>
          <p className={local.note}>
            Only facts the clinic approved, one per line, in the order the app
            shows them (no ratings or praise).
          </p>
          {lines.length > 0 && (
            <ol className={styles.lines} ref={listRef}>
              {lines.map((line, i) => (
                <li key={line.id} className={styles.line}>
                  <Field
                    label={`Qualification ${i + 1}`}
                    error={errors.lines[i]}
                  >
                    <Input
                      value={line.text}
                      maxLength={DOCTOR_PROFILE_LIMITS.maxLineLength}
                      placeholder="e.g. MBBS (Malaya)"
                      onChange={(e) =>
                        edit(
                          lines.map((l) =>
                            l.id === line.id
                              ? { ...l, text: e.target.value }
                              : l,
                          ),
                        )
                      }
                    />
                  </Field>
                  <span className={styles.lineActions}>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<ArrowUp size={16} />}
                      aria-label={`Move qualification ${i + 1} up`}
                      disabled={i === 0}
                      onClick={() => edit(move(lines, i, -1))}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<ArrowDown size={16} />}
                      aria-label={`Move qualification ${i + 1} down`}
                      disabled={i === lines.length - 1}
                      onClick={() => edit(move(lines, i, 1))}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<X size={16} />}
                      aria-label={`Remove qualification ${i + 1}`}
                      onClick={() => edit(lines.filter((_, j) => j !== i))}
                    />
                  </span>
                </li>
              ))}
            </ol>
          )}
          {errors.list && (
            <p className={styles.listError} role="alert">
              {errors.list}
            </p>
          )}
          <div className={styles.listFooter}>
            <span>
              {lines.length} of {max} lines
            </span>
            <Button
              size="sm"
              icon={<Plus size={16} />}
              disabled={lines.length >= max}
              onClick={() => {
                edit([...lines, newLine('')]);
                focusLine(lines.length);
              }}
            >
              Add qualification
            </Button>
          </div>
        </>
      )}

      <h3 className={styles.section}>Photo</h3>
      <DoctorPhotoField
        staffId={staffId}
        photoUrl={p.photoUrl}
        readOnly={!canEdit}
      />
    </>
  );
  return canEdit ? (
    <form
      id={formId}
      className={local.form}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {content}
    </form>
  ) : (
    <div className={local.form}>{content}</div>
  );
}
